import { useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';
import { fetchWithAdmin } from '../../api/fetchHelper';
import type { PromptInfo } from '../../types/settings';
import type { PromptType } from '@padcanvas/shared';
import { PROMPT_TYPE_LABELED_OPTIONS } from '../../types/promptTypeLabels';
import { LatexText } from '@shared-ui/latex';
import { ImageViewer, IntegerInput, NumberInput } from '@shared-ui/components';
import './Playground.css';

interface PlaygroundProps {
  questionId: string | null;
  submissionId: string | null;
}

interface PlaygroundResult {
  modelOutputId: string;
  builtPrompt: string;
  rawResponse: string;
  parsedFeedback: unknown;
  parseError?: string;
  tokenUsage?: { inputTokens: number; outputTokens: number; totalTokens: number };
  latencyMs: number;
  estimatedCostUsd: number;
  placeholderContext: Record<string, unknown>;
  hasImage?: boolean;
}

// Source of truth for the dropdown lives in promptTypeLabels.ts.
const PROMPT_TYPES = PROMPT_TYPE_LABELED_OPTIONS;

export function Playground({ questionId, submissionId }: PlaygroundProps) {
  // Prompt config
  const [userPrompt, setUserPrompt] = useState('');
  const [systemPrompt, setSystemPromptState] = useState('');
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const [provider, setProvider] = useState('claude-sonnet-4-5');
  const [promptType, setPromptType] = useState<PromptType>('analyze_image_text_objective');
  const [temperature, setTemperature] = useState(0.3);
  const [maxTokens, setMaxTokens] = useState(2000);

  // Run history
  const [runs, setRuns] = useState<PlaygroundResult[]>([]);
  const [selectedRunIdx, setSelectedRunIdx] = useState<number | null>(null);
  const [resultTab, setResultTab] = useState<'parsed' | 'raw' | 'built' | 'context'>('parsed');

  // Load providers (detailed: key + displayName + modelName)
  const { data: providersDetailed } = useQuery({
    queryKey: ['available-providers-detailed'],
    queryFn: adminApi.getAvailableProvidersDetailed,
  });

  // Load prompts for "load from existing" feature
  const { data: prompts } = useQuery({
    queryKey: ['prompts-with-templates'],
    queryFn: () => adminApi.getPrompts({ includeTemplate: true }),
  });

  // Load question details
  const { data: question } = useQuery({
    queryKey: ['question', questionId],
    queryFn: () => adminApi.getQuestionById(questionId!),
    enabled: !!questionId,
  });

  // Load submission details
  const { data: submission } = useQuery({
    queryKey: ['submission', submissionId],
    queryFn: () => adminApi.getSubmission(submissionId!),
    enabled: !!submissionId,
  });

  // Run mutation
  const runMutation = useMutation({
    mutationFn: (payload: {
      questionId: string;
      userPrompt: string;
      systemPrompt?: string;
      provider: string;
      promptType: PromptType;
      submissionId?: string;
      temperature: number;
      maxTokens: number;
    }) => fetchWithAdmin<PlaygroundResult>('/console/prompt-playground/run', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
    onSuccess: (data) => {
      setRuns(prev => [data, ...prev]);
      setSelectedRunIdx(0);
      setResultTab('parsed');
    },
  });

  const handleRun = useCallback(() => {
    if (!questionId || !userPrompt.trim()) return;
    runMutation.mutate({
      questionId,
      userPrompt,
      systemPrompt: systemPrompt.trim() || undefined,
      provider,
      promptType,
      submissionId: submissionId || undefined,
      temperature,
      maxTokens,
    });
  }, [questionId, userPrompt, systemPrompt, provider, promptType, submissionId, temperature, maxTokens]);

  const handleLoadPrompt = useCallback((promptInfo: PromptInfo) => {
    if (promptInfo.userPrompt) {
      setUserPrompt(promptInfo.userPrompt);
      setSystemPromptState(promptInfo.systemPrompt || '');
      setPromptType(promptInfo.type);
    }
  }, []);

  const availableProviders = providersDetailed
    ? providersDetailed.filter(p => p.available)
    : [{ key: 'claude-sonnet-4-5', displayName: 'Claude Sonnet 4.5', modelName: 'claude-sonnet-4-5', family: 'anthropic', available: true }];

  const selectedRun = selectedRunIdx !== null ? runs[selectedRunIdx] : null;

  if (!questionId) {
    return (
      <div className="playground-page">
        <div className="playground-empty">
          Open a submission and click <strong>Playground</strong> to experiment with custom-play-prompts.
        </div>
      </div>
    );
  }

  return (
    <div className="playground-page">
      {/* Left Panel — Question + Student + Context */}
      <div className="playground-left">
          <div className="playground-section playground-section-no-border">
            <div className="playground-section-header">
              <h3>Question</h3>
              {submissionId && (
                <Link to={`/submissions/${submissionId}`} className="playground-submission-link">
                  View Submission
                </Link>
              )}
            </div>
            {question ? (
              <div className="playground-selected-question">
                <div className="playground-selected-header">
                  <span className="playground-badge">{question.subject}</span>
                  <span className="playground-badge">{question.difficulty || 'medium'}</span>
                  <span className="playground-badge">{question.questionType || 'FREEFORM'}</span>
                </div>
                <div className="playground-q-detail">
                  <LatexText text={question.questionText} />
                </div>
                <div className="playground-q-detail">
                  <strong>Answer:</strong> {question.correctAnswer}
                </div>
              </div>
            ) : (
              <div className="playground-hint">Loading question...</div>
            )}
          </div>

          <div className="playground-section">
            <h3>Context {selectedRun ? '(resolved)' : ''}</h3>
            {selectedRun ? (
              <ContextTable context={selectedRun.placeholderContext} hasImage={selectedRun.hasImage} submission={submission} />
            ) : question ? (
              <ContextTable context={buildPreviewContext(question, submission)} hasImage={!!submission?.canvasImageUrl} submission={submission} />
            ) : (
              <div className="playground-hint">Loading...</div>
            )}
          </div>
      </div>

      {/* Right Panel — Editor & Results */}
      <div className="playground-right">
        {/* Config Bar */}
        <div className="playground-config-bar">
          <div className="playground-config-left">
            <select
              value={promptType}
              onChange={(e) => setPromptType(e.target.value as PromptType)}
              className="playground-select"
            >
              {PROMPT_TYPES.map(pt => (
                <option key={pt.value} value={pt.value}>{pt.label}</option>
              ))}
            </select>

            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              className="playground-select"
            >
              {availableProviders.map(p => (
                <option key={p.key} value={p.key}>{p.displayName} ({p.modelName})</option>
              ))}
            </select>

            <select
              onChange={(e) => {
                const prompt = prompts?.find((p: PromptInfo) => p.id === e.target.value);
                if (prompt) handleLoadPrompt(prompt);
                e.target.value = '';
              }}
              className="playground-select"
              defaultValue=""
            >
              <option value="" disabled>Load prompt...</option>
              {prompts?.map((p: PromptInfo) => (
                <option key={p.id} value={p.id}>{p.name} (v{p.version})</option>
              ))}
            </select>
          </div>

          <div className="playground-params">
            <label className="playground-param" title="Temperature (0 = deterministic, 1 = creative)">
              <span>Temp</span>
              <NumberInput
                value={temperature}
                onChange={(v) => { if (v != null) setTemperature(v); }}
                min={0} max={2} step={0.1}
                className="playground-param-input"
              />
            </label>
            <label className="playground-param" title="Maximum output tokens">
              <span>Max</span>
              <IntegerInput
                value={maxTokens}
                onChange={(v) => { if (v != null) setMaxTokens(v); }}
                min={100} max={16000} step={100}
                className="playground-param-input"
              />
            </label>
          </div>

          <button
            className="playground-run-btn"
            onClick={handleRun}
            disabled={!userPrompt.trim() || runMutation.isPending}
          >
            {runMutation.isPending ? 'Running...' : 'Run'}
          </button>
        </div>

        {/* Run History */}
        <div className="playground-right-upper">
          {runs.length > 0 && (
            <div className="playground-section playground-section-compact">
              <h3>Runs ({runs.length})</h3>
              <div className="playground-runs-list">
                {runs.map((run, idx) => (
                  <button
                    key={run.modelOutputId}
                    className={`playground-run-item${selectedRunIdx === idx ? ' active' : ''}`}
                    onClick={() => { setSelectedRunIdx(idx); setResultTab('parsed'); }}
                  >
                    <span className="playground-run-meta">
                      {(run.latencyMs / 1000).toFixed(1)}s
                      {run.tokenUsage ? ` · ${run.tokenUsage.totalTokens} tok` : ''}
                      {run.estimatedCostUsd ? ` · $${run.estimatedCostUsd.toFixed(4)}` : ''}
                    </span>
                    <span className="playground-run-status">
                      {run.parseError ? 'Parse error' : run.parsedFeedback ? 'OK' : 'Raw'}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* System Prompt Editor (optional) */}
        <div className="playground-editor-section" style={{ flex: '0 0 auto' }}>
          <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', padding: '4px 8px' }}>System prompt (optional)</div>
          <textarea
            value={systemPrompt}
            onChange={(e) => setSystemPromptState(e.target.value)}
            placeholder="Optional. Persona / rules / output contract — sent as instructions/system."
            className="playground-editor"
            style={{ minHeight: '120px', maxHeight: '200px' }}
            spellCheck={false}
          />
        </div>

        {/* User Prompt Editor */}
        <div className="playground-editor-section">
          <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', padding: '4px 8px' }}>User prompt</div>
          <textarea
            ref={editorRef}
            value={userPrompt}
            onChange={(e) => setUserPrompt(e.target.value)}
            placeholder="Enter your user prompt template here... Use {{placeholders}} for dynamic content.&#10;&#10;Or select an existing prompt from the dropdown above."
            className="playground-editor"
            spellCheck={false}
          />
        </div>

        {/* Results */}
        {runMutation.isError && (
          <div className="playground-error">
            Error: {(runMutation.error as Error).message}
          </div>
        )}

        {selectedRun && (
          <div className="playground-results">
            <div className="playground-results-header">
              <div className="playground-result-tabs">
                <button className={resultTab === 'parsed' ? 'active' : ''} onClick={() => setResultTab('parsed')}>Parsed</button>
                <button className={resultTab === 'raw' ? 'active' : ''} onClick={() => setResultTab('raw')}>Raw</button>
                <button className={resultTab === 'built' ? 'active' : ''} onClick={() => setResultTab('built')}>Built Prompt</button>
              </div>
              <div className="playground-result-meta">
                <span>{(selectedRun.latencyMs / 1000).toFixed(1)}s</span>
                {selectedRun.tokenUsage && (
                  <span>{selectedRun.tokenUsage.totalTokens} tokens</span>
                )}
                {selectedRun.estimatedCostUsd > 0 && (
                  <span>${selectedRun.estimatedCostUsd.toFixed(4)}</span>
                )}
                {selectedRun.parseError && <span className="playground-parse-error">Parse error</span>}
              </div>
            </div>
            <div className="playground-result-content">
              {resultTab === 'parsed' && (
                <pre className="playground-result-pre">
                  {selectedRun.parsedFeedback
                    ? JSON.stringify(selectedRun.parsedFeedback, null, 2)
                    : selectedRun.rawResponse || '(empty response)'}
                </pre>
              )}
              {resultTab === 'raw' && (
                <div className="playground-result-pre"><LatexText text={selectedRun.rawResponse || '(empty)'} /></div>
              )}
              {resultTab === 'built' && (
                <pre className="playground-result-pre">{selectedRun.builtPrompt}</pre>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Build a preview context from already-loaded question + submission data */
function buildPreviewContext(question: any, submission: any): Record<string, unknown> {
  const ctx: Record<string, unknown> = {
    question: {
      subject: question.subject,
      topic: question.topic || '',
      difficulty: question.difficulty || 'medium',
      questionText: question.questionText,
      correctAnswer: question.correctAnswer || '',
      questionType: question.questionType || 'FREEFORM',
    },
  };

  if (question.solutions?.[0]) {
    ctx.solution = {
      steps: question.solutions[0].steps,
      expectedTimeSeconds: question.solutions[0].expectedTimeSeconds,
    };
  }

  if (submission) {
    ctx.std = {
      hasImage: !!submission.canvasImageUrl,
      typedText: submission.typedText || undefined,
      actualTimeSeconds: submission.timeTakenSeconds,
    };
    ctx.analysis = {
      score: submission.feedback?.computedScores?.finalScore,
      llmAssessment: submission.feedback?.llmAssessment || undefined,
      computedScores: submission.feedback?.computedScores || undefined,
    };
  }

  return ctx;
}

/** Two-column field/value display for placeholder context */
function ContextTable({ context, hasImage, submission }: { context: Record<string, unknown>; hasImage?: boolean; submission?: any }) {
  const rows: { group: string; field: string; value: string }[] = [];

  for (const [group, groupObj] of Object.entries(context)) {
    if (!groupObj || typeof groupObj !== 'object') {
      rows.push({ group, field: group, value: String(groupObj) });
      continue;
    }
    for (const [field, value] of Object.entries(groupObj as Record<string, unknown>)) {
      if (value === undefined || value === null) continue;
      const display = typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
      rows.push({ group, field, value: display });
    }
  }

  // Image from submission's canvas
  const canvasUrl = submission?.canvasImageUrl;

  return (
    <div className="playground-context-table">
      {hasImage && (
        <div className="playground-context-image-row">
          <span className="playground-ctx-label">Student Image</span>
          <div className="playground-ctx-value">
            {canvasUrl ? (
              <ImageViewer src={canvasUrl} alt="Student canvas" className="playground-ctx-image" />
            ) : (
              <span style={{ color: 'var(--text-tertiary)' }}>Image was sent to LLM (not available for preview)</span>
            )}
          </div>
        </div>
      )}
      {rows.map((row, i) => (
        <div key={i} className="playground-context-row">
          <span className="playground-ctx-label">{row.group}.{row.field}</span>
          <span className="playground-ctx-value">
            {row.value.includes('\n') ? <pre className="playground-ctx-pre">{row.value}</pre> : row.value}
          </span>
        </div>
      ))}
    </div>
  );
}
